import { useDebounceEffect, useLatest } from 'ahooks';
import React, { useState } from 'react';
import { useFetcher } from 'react-router';
import type Variant from '~/modules/product/type/variant';
import { useSampleStock } from '~/modules/product/hook/use-sample-stock';
import type { ShippingProviderEnum } from '../../../../generated/graphql';
import { useHydrated } from 'remix-utils';

export interface ShippingMethodType {
    ID: number;
    Title: string;
    ShippingCost?: {
        Amount: number;
        ShortFormat: string;
    } | null;
    ShippingCostText: string;
    Fulfilled: boolean;
    DeliveryEstimateText: string;
    FreeThresholdEnabled: boolean;
    QuotedSeparately: boolean;
    ShippingProvider?: ShippingProviderEnum | null;
}

interface VariantShippingMethodsState {
    shippingMethods: ShippingMethodType[];
    sampleShippingMethods: ShippingMethodType[];
    isLoading: boolean;
}

interface VariantShippingMethodsProviderProps {
    children: React.ReactNode;
    selectedVariant: Variant | null;
    quantity: number;
    estimatedSampleWeight?: number;
}

interface FetcherMethod {
    ID: number;
    ShippingProvider: ShippingProviderEnum | null;
}

type FetcherResult = Array<{
    ShippingCost: {
        Amount: number;
        ShortFormat: string;
    };
    ShippingCostText: string;
    ShippingMethod: FetcherMethod;
}> | null;

type FetcherType = ReturnType<typeof useFetcher<FetcherResult>>;

const VariantShippingMethodsContext = React.createContext<
    VariantShippingMethodsState | undefined
>(undefined);

export const useVariantShippingMethods = () => {
    const context = React.useContext(VariantShippingMethodsContext);
    if (!context) {
        throw new Error('Cannot be used outside VariantShippingMethodsContext');
    }
    return context;
};

const validateMethod = (
    method: ShippingMethodType,
    packageWeight = 0,
): boolean =>
    method.ShippingCost !== null || (!method.Fulfilled && packageWeight > 0);

const matchShippingMethods = (
    fetchedMethod: FetcherMethod,
    method: ShippingMethodType,
): boolean => {
    return !!(
        fetchedMethod.ID === method.ID ||
        (fetchedMethod.ID === 0 &&
            fetchedMethod.ShippingProvider &&
            fetchedMethod.ShippingProvider === method.ShippingProvider)
    );
};

const mergeShippingMethod = (
    shippingMethods: ShippingMethodType[],
    fetcher: FetcherType,
) => {
    if (fetcher.state !== 'idle') {
        return null;
    }
    // Soft-fail from the route: keep SSR methods so the product page stays usable.
    if (fetcher.data === null) {
        return shippingMethods;
    }
    // No fetch result yet.
    if (fetcher.data === undefined) {
        return null;
    }

    const resolvedMethods = fetcher.data;

    // Legitimate empty list: drop null-cost methods that failed to price.
    return shippingMethods
        .map((m: ShippingMethodType) => {
            const refreshedMethod = resolvedMethods.find((rm) =>
                matchShippingMethods(rm.ShippingMethod, m),
            );
            if (refreshedMethod) {
                return {
                    ...m,
                    ShippingCost: {
                        ...refreshedMethod.ShippingCost,
                        __typename: 'CurrencyAmount',
                    },
                    ShippingCostText: refreshedMethod.ShippingCostText,
                };
            }
            return m.ShippingCost ? m : null;
        })
        .filter((m: ShippingMethodType | null): m is ShippingMethodType => !!m);
};

const fetchUnfulfilledShippingRates = (
    fetcher: FetcherType,
    shippingMethods: ShippingMethodType[],
    variantID: number,
    quantity: number,
    isSample: boolean,
): boolean => {
    const formData = new FormData();
    if (!variantID || quantity <= 0) {
        return false;
    }

    const unfulfilledShippingMethods = shippingMethods.filter(
        (m) => !m.Fulfilled,
    );
    if (!unfulfilledShippingMethods.length) {
        return false;
    }

    formData.append('variantID', variantID.toString());
    formData.append('quantity', quantity.toString());
    formData.append('isSample', isSample ? '1' : '0');

    unfulfilledShippingMethods.forEach((m) => {
        formData.append('methodIDs', m.ID.toString());
    });

    fetcher.submit(formData, {
        method: 'GET',
        action: '/remix-api/ecommerce/variant-shipping-get',
    });

    return true;
};

export const VariantShippingMethodsProvider = ({
    children,
    quantity,
    selectedVariant,
    estimatedSampleWeight,
}: VariantShippingMethodsProviderProps) => {
    const fetcher = useFetcher<FetcherResult>();
    const sampleFetcher = useFetcher<FetcherResult>();
    const fetcherRef = useLatest(fetcher);
    const sampleFetcherRef = useLatest(sampleFetcher);
    const hydrated = useHydrated();
    const { canPurchase: hasSamples } = useSampleStock(selectedVariant);

    const packageWeight = selectedVariant?.Size?.PackageWeight ?? 0;

    const validShippingMethods =
        selectedVariant?.ShippingProfile?.ShippingMethods.filter((m) =>
            validateMethod(m, packageWeight),
        ) ?? [];
    const validSampleShippingMethods =
        selectedVariant?.SampleShippingProfile?.ShippingMethods.filter((m) =>
            validateMethod(m, estimatedSampleWeight ?? 0),
        ) ?? [];

    const validMethodsRef = useLatest({
        shippingMethods: validShippingMethods,
        sampleShippingMethods: validSampleShippingMethods,
    });

    const [shippingMethods, setShippingMethods] =
        React.useState<ShippingMethodType[]>(validShippingMethods);
    const [sampleShippingMethods, setSampleShippingMethods] = React.useState<
        ShippingMethodType[]
    >(validSampleShippingMethods);

    const [loadingState, setLoadingState] = useState(false);

    // Need fetcher loading states and loading state, if not we get flickering
    const loading =
        sampleFetcher.state === 'loading' ||
        fetcher.state === 'loading' ||
        loadingState;

    const value = React.useMemo(() => {
        return {
            shippingMethods,
            sampleShippingMethods,
            isLoading: loading,
        };
    }, [shippingMethods, sampleShippingMethods, loading]);

    React.useEffect(() => {
        setShippingMethods(validMethodsRef.current.shippingMethods);
        setSampleShippingMethods(validMethodsRef.current.sampleShippingMethods);
    }, [selectedVariant, validMethodsRef]);

    React.useEffect(() => {
        setLoadingState(true);
    }, [quantity, selectedVariant, hasSamples]);

    // Fetch variant shipping methods
    useDebounceEffect(() => {
        if (!hydrated || !selectedVariant) return;
        setLoadingState(true);
        const originalMethods = validMethodsRef.current;
        const hasUnfulfilled = fetchUnfulfilledShippingRates(
            fetcherRef.current,
            originalMethods.shippingMethods,
            selectedVariant.ID,
            quantity,
            false,
        );
        setLoadingState(hasUnfulfilled);
    }, [quantity, hydrated, fetcherRef, selectedVariant]);

    // Fetch sample shipping methods
    useDebounceEffect(() => {
        if (!hydrated || !selectedVariant || !hasSamples) return;
        setLoadingState(true);
        const originalMethods = validMethodsRef.current;
        const hasUnfulfilled = fetchUnfulfilledShippingRates(
            sampleFetcherRef.current,
            originalMethods.sampleShippingMethods,
            selectedVariant.ID,
            1,
            true,
        );
        setLoadingState(hasUnfulfilled);
    }, [hasSamples, hydrated, sampleFetcherRef, selectedVariant]);

    React.useEffect(() => {
        const mergedMethods = mergeShippingMethod(
            validMethodsRef.current.shippingMethods,
            fetcher,
        );
        if (mergedMethods) {
            setShippingMethods(mergedMethods);
            setLoadingState(false);
        }
    }, [fetcher, validMethodsRef]);

    React.useEffect(() => {
        const mergedMethods = mergeShippingMethod(
            validMethodsRef.current.sampleShippingMethods,
            sampleFetcher,
        );
        if (mergedMethods) {
            setSampleShippingMethods(mergedMethods);
            setLoadingState(false);
        }
    }, [sampleFetcher, validMethodsRef]);

    return (
        <VariantShippingMethodsContext.Provider value={value}>
            {children}
        </VariantShippingMethodsContext.Provider>
    );
};
